import React, { useState, useEffect, useRef } from 'react';

import { useTranslation } from 'react-i18next';
import { DateTime } from 'luxon';
import classNames from 'classnames';

import { calculateNodeOffset } from 'clean/utils/functions';
import Paper from 'clean/components/common/Paper/Paper';
import EntryTitle from 'clean/components/common/EntryTitle/EntryTitle';
import TextField from 'clean/components/common/TextField/TextField';

import './DetailsPopup.scoped.scss';
import i18n from 'translations/i18n';
import DropDown from 'clean/components/common/DropDown/DropDown';
import { ReactComponent as AbsentIcon } from 'assets/icons/attendance_absent.svg';
import { ReactComponent as LateIcon } from 'assets/icons/attendance_late.svg';
import { ReactComponent as ExemptIcon } from 'assets/icons/attendance_exempt.svg';
import { ReactComponent as RemarkIcon } from 'assets/icons/attendance_remark.svg';

interface Props {
  gradeItem?: any;
  rootNode: React.RefObject<HTMLElement>;
  onClose: () => void;
}

const Details: React.FC<Props> = ({ rootNode, onClose, gradeItem }) => {
  const topOffset = 90;
  const detailsNode = useRef(null);
  const { t } = useTranslation();

  const [offset, setOffset] = useState({ x: null, y: null });

  const updateOffset = () => {
    if (detailsNode.current) {
      const nodeOffsets = calculateNodeOffset(rootNode.current);
      let top = nodeOffsets.y - topOffset;
      let left = nodeOffsets.x + rootNode.current.offsetWidth - 1; // -1 is so the border doesn't glitch out when running non-native resolution

      // Means that there is a collision on right side, and we should move it back to the left side
      if (window.innerWidth - left < detailsNode.current.offsetWidth) {
        left -= detailsNode.current.offsetWidth + rootNode.current.offsetWidth - 1; // The same reason
      }

      if (window.innerHeight - top < detailsNode.current.offsetHeight) {
        top -= detailsNode.current.offsetHeight - (window.innerHeight - top) + 10;
      }

      setOffset({ x: left, y: top });
    }
  };

  useEffect(updateOffset, [detailsNode.current]);

  const getIcon = (type, styles) => {
    return {
      absent: <AbsentIcon style={styles} />,
      late: <LateIcon style={styles} />,
      exempt: <ExemptIcon style={styles} />,
      remark: <RemarkIcon style={styles} />,
    }[type];
  };

  return (
    <div
      ref={detailsNode}
      style={{ '--offset-x': offset.x, '--offset-y': offset.y } as React.CSSProperties}
      className="grade-details-wrapper"
    >
      <div
        onClick={e => {
          e.stopPropagation();
          onClose();
        }}
        className="backdrop-overlay"
      ></div>
      <div className="wrapper">
        <Paper>
          <div className="content">
            <div className="title">{t('attendance:attendance')}</div>

            <div
              className="close"
              onClick={e => {
                e.stopPropagation();
                onClose();
              }}
            />

            <div className="entry">
              <EntryTitle title={t('common:Date')}>
                {
                  <TextField
                    disabled
                    value={DateTime.fromISO(gradeItem?.date)
                      .setLocale(i18n.language)
                      .toFormat('MM.dd.y')}
                    onChange={() => null}
                  />
                }
              </EntryTitle>
            </div>

            <div className="entry">
              <EntryTitle title={t('grades:class')}>
                <TextField disabled value={gradeItem?.class?.title} onChange={() => null} />
              </EntryTitle>
            </div>
            <div className="entry">
              <EntryTitle title={t('attendance:subject')}>
                <TextField disabled value={gradeItem?.subject?.title} onChange={() => null} />
              </EntryTitle>
            </div>
            <div className="entry">
              <EntryTitle title={t('common:Teacher')}>
                <TextField
                  disabled
                  value={gradeItem.subject?.teacher_subjects[0]?.teacher?.user?.full_name}
                  onChange={() => null}
                />
              </EntryTitle>
            </div>

            <div className="entry">
              <EntryTitle title={t('attendance:attendanceType')}>
                <div style={{ opacity: 1, pointerEvents: 'none' }}>
                  <DropDown
                    showChevron={false}
                    options={[
                      {
                        ...gradeItem.type,
                        title: gradeItem?.type?.text,
                      },
                    ]}
                    labelProperty="title"
                    idProperty="value"
                    value={gradeItem?.type?.value}
                    iconStart={getIcon(gradeItem?.type?.icon, { marginLeft: '4px' })}
                    emptyItemLabel={t('attendance:select')}
                    disabled={true}
                    onChange={() => null}
                    renderRow={({
                      item: attendanceType,
                      classes: defaultClasses,
                      onClick,
                      isSelected,
                    }) => {
                      return (
                        <div
                          key={attendanceType.value}
                          onClick={() => onClick(attendanceType)}
                          className={classNames(defaultClasses.outer, {
                            [defaultClasses.outerSelected]: isSelected,
                          })}
                        >
                          <div className={defaultClasses.inner}>
                            {attendanceType.icon &&
                              getIcon(attendanceType.icon, { marginRight: '10px' })}
                            {attendanceType.title}
                          </div>
                        </div>
                      );
                    }}
                  />
                </div>
              </EntryTitle>
            </div>

            <div className="entry">
              <EntryTitle title={t('common:Comment')}>
                <TextField
                  multiLine
                  value={gradeItem?.comment}
                  onChange={() => null}
                  disabled={true}
                />
              </EntryTitle>
            </div>
          </div>
        </Paper>
      </div>
    </div>
  );
};

export default Details;
